import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPrinters,
  getTableInfo,
  createPrinter,
  updatePrinter,
  deletePrinter,
} from "apis/printAPIs";
import { showErrorSnackbar, showSuccessSnackbar } from "store/actions/snackbarMessage";
import { BackButton } from "components";
import ZoneManagement from "./ZoneManagement";





import { noWheelInputProps } from "util/utils";
const savePrinterTags = async ({ printerId, tags }) => {
  const response = await fetch(`/api/printers/${printerId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tags }),
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};



const POSPrinterSettings = () => {
  const dispatch = useDispatch();
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [floors, setFloors] = useState([]);
  const [customDeviceName, setCustomDeviceName] = useState("");
  const [isCustomDevice, setIsCustomDevice] = useState(false);
  const [nameError, setNameError] = useState("");
  
  // State for printer dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPrinter, setCurrentPrinter] = useState({
    printerName: '',
    deviceName: 'TM-T82IV', // Default value updated
    ipAddress: '',
    brand: 'epson', // Default value updated
    isPrimary: false,
    epsonLanguage: 0,
    tags: [],
    port: "" // Add default port
  });

  // Add state for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [printerToDelete, setPrinterToDelete] = useState(null);

  const { data: printers, isLoading } = useQuery({
    queryKey: ["printers"],
    queryFn: getPrinters
  });
  const { data: tableInfo } = useQuery({
    queryKey: ["tables"],
    queryFn: getTableInfo
  });

  const tagsMutation = useMutation({
    mutationFn: savePrinterTags
  });
  
  const queryClient = useQueryClient();
  
  // Mutations for create and update
  const createMutation = useMutation({
    mutationFn: createPrinter,
    onSuccess: () => {
      queryClient.invalidateQueries('printers');
      dispatch(showSuccessSnackbar("Printer created successfully"));
      handleCloseDialog();
    },
    onError: (error) => {
      console.error('Error creating printer:', error);
      
      // Handle specific error cases
      if (error.response?.data?.message?.includes('duplicate')) {
        setNameError("A printer with this name already exists");
        dispatch(showErrorSnackbar("A printer with this name already exists"));
      } else if (error.response?.status === 400) {
        dispatch(showErrorSnackbar("Invalid printer configuration. Please check all fields."));
      } else if (error.response?.status === 403) {
        dispatch(showErrorSnackbar("You don't have permission to create printers"));
      } else if (error.response?.status >= 500) {
        dispatch(showErrorSnackbar("Server error. Please try again later."));
      } else if (error.message === 'Network Error' || !error.response) {
        dispatch(showErrorSnackbar("Network error. Please check your connection and try again."));
      } else {
        const errorMessage = error.response?.data?.message || error.message || "Failed to create printer";
        dispatch(showErrorSnackbar(`Failed to create printer: ${errorMessage}`));
      }
    }
  });
  
  const updateMutation = useMutation({
    mutationFn: updatePrinter,
    onSuccess: () => {
      queryClient.invalidateQueries('printers');
      dispatch(showSuccessSnackbar("Printer updated successfully"));
      handleCloseDialog();
    },
    onError: (error) => {
      console.error('Error updating printer:', error);
      
      // Handle specific error cases
      if (error.response?.data?.message?.includes('duplicate')) {
        setNameError("A printer with this name already exists");
        dispatch(showErrorSnackbar("A printer with this name already exists"));
      } else if (error.response?.status === 400) {
        dispatch(showErrorSnackbar("Invalid printer configuration. Please check all fields."));
      } else if (error.response?.status === 403) {
        dispatch(showErrorSnackbar("You don't have permission to update printers"));
      } else if (error.response?.status === 404) {
        dispatch(showErrorSnackbar("Printer not found"));
      } else if (error.response?.status >= 500) {
        dispatch(showErrorSnackbar("Server error. Please try again later."));
      } else if (error.message === 'Network Error' || !error.response) {
        dispatch(showErrorSnackbar("Network error. Please check your connection and try again."));
      } else {
        const errorMessage = error.response?.data?.message || error.message || "Failed to update printer";
        dispatch(showErrorSnackbar(`Failed to update printer: ${errorMessage}`));
      }
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deletePrinter,
    onSuccess: () => {
      queryClient.invalidateQueries('printers');
      dispatch(showSuccessSnackbar("Printer deleted successfully"));
      handleCloseDeleteDialog();
    },
    onError: (error) => {
      console.error('Error deleting printer:', error);
      
      if (error.response?.status === 403) {
        dispatch(showErrorSnackbar("You don't have permission to delete printers"));
      } else if (error.response?.status === 404) {
        dispatch(showErrorSnackbar("Printer not found"));
      } else if (error.response?.status >= 500) {
        dispatch(showErrorSnackbar("Server error. Please try again later."));
      } else if (error.message === 'Network Error' || !error.response) {
        dispatch(showErrorSnackbar("Network error. Please check your connection and try again."));
      } else {
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete printer";
        dispatch(showErrorSnackbar(`Failed to delete printer: ${errorMessage}`));
      }
    }
  });

  const handleSaveTags = (printerId) => {
    const tags = [
      ...selectedTags,
      ...`floor-${selectedFloor}`,
      ...selectedTables.map((table) => `${table}`),
    ];
    tagsMutation.mutate({ printerId, tags });
  };

  const handleOpenCreateDialog = () => {
    setCurrentPrinter({
      printerName: '',
      deviceName: 'TM-T82III', // Updated default value
      ipAddress: '',
      brand: 'EPSON',
      isPrimary: false,
      tags: [],
      port: "" // Add default port
    });
    setIsCustomDevice(false);
    setCustomDeviceName("");
    setIsEditMode(false);
    setNameError("");
    setDialogOpen(true);
  };
  
  const handleOpenEditDialog = (printer) => {
    setCurrentPrinter({...printer});
    // Check if the printer's device name is in our predefined list
    const predefinedDevices = ["TM-T82III", "TM-T82IIIL", "TM-T82II", "TM-m30", "TM-T82IV", 
                              "TM-m30II", "TM-T20", "TM-T88VI", "TM-T88V", "TM-T82", 
                              "TM-m30II-H", "TM-U220"];
    
    if (!predefinedDevices.includes(printer.deviceName)) {
      setIsCustomDevice(true);
      setCustomDeviceName(printer.deviceName);
      setCurrentPrinter(prev => ({
        ...prev,
        deviceName: "other"
      }));
    } else {
      setIsCustomDevice(false);
      setCustomDeviceName("");
    }
    
    setIsEditMode(true);
    setNameError("");
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsCustomDevice(false);
    setCustomDeviceName("");
    setNameError("");
    // Clear any previous errors when closing dialog
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "deviceName" && value === "other") {
      setIsCustomDevice(true);
      setCurrentPrinter(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === "deviceName" && isCustomDevice) {
      setIsCustomDevice(false);
      setCustomDeviceName("");
      setCurrentPrinter(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === "customDeviceName") {
      setCustomDeviceName(value);
      setCurrentPrinter(prev => ({
        ...prev,
        deviceName: value
      }));
    } else if (name === "printerName") {
      // Clear any previous name error when user types
      setNameError("");
      setCurrentPrinter(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === "epsonLanguage") {
      setCurrentPrinter(prev => ({
        ...prev,
        epsonLanguage: parseInt(value)
      }));
    } else if (name === "port") {
      setCurrentPrinter(prev => ({
        ...prev,
        port: String(value)
      }));
    } else {
      setCurrentPrinter(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const validatePrinterName = () => {
    if (!printers) return true;
    
    const nameExists = printers.some(printer => 
      printer.printerName.toLowerCase() === currentPrinter.printerName.toLowerCase() && 
      (!isEditMode || printer.printerName !== currentPrinter.printerName)
    );
    
    if (nameExists) {
      setNameError("A printer with this name already exists");
      return false;
    }
    
    return true;
  };
  
  const handleSavePrinter = () => {
    // Validate printer name uniqueness
    if (!validatePrinterName()) {
      return;
    }
    
    const printerToSave = {...currentPrinter};
    
    // If using custom device name, update the deviceName
    if (isCustomDevice && customDeviceName) {
      printerToSave.deviceName = customDeviceName;
    }
    
    if (isEditMode) {
      updateMutation.mutate(printerToSave);
    } else {
      createMutation.mutate(printerToSave);
    }
  };

  const handleOpenDeleteDialog = (printer) => {
    setPrinterToDelete(printer);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPrinterToDelete(null);
  };
  
  const handleDeletePrinter = () => {
    if (!printerToDelete) return;
    const identifier = printerToDelete.printerName || printerToDelete.ipAddress;
    if (identifier) {
      deleteMutation.mutate(identifier);
    }
  };

  useEffect(() => {
    if (tableInfo) {
      const areas = tableInfo.areas || [];
      const areaList = areas.map((area) => {
        const tables = area.hasTables.map((table) => table.tableNumber);
        return { name: area.areaName, tables };
      });
      setFloors(areaList);
    }
  }, [tableInfo]);

  const relatedTables =
    selectedArea && selectedArea !== ""
      ? floors.find((area) => area.name === selectedArea)?.tables
      : [];

  if (isLoading) return <LinearProgress />;

  return (
    <div>
      <Box padding={3}>
        <BackButton to="/myrestaurant" label="My Restaurant" />
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="20px">
          <Typography variant="h4">
            Printer Settings
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Add Printer
          </Button>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Device Type</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {printers?.map((printer) => (
              <TableRow key={printer.printerName}>
                <TableCell>{printer.printerName}</TableCell>
                <TableCell>{printer.deviceName}</TableCell>
                <TableCell>{printer.ipAddress}</TableCell>
                <TableCell>{printer.brand}</TableCell>
                <TableCell>
                  <Chip 
                    label={printer.isPrimary ? "Primary" : "Secondary"} 
                    size="small"
                    color={printer.isPrimary ? "secondary" : "default"}
                  />
                </TableCell>
                <TableCell>{printer.epsonLanguage === 0 ? "English" : printer.epsonLanguage === 1 ? "Japanese" : printer.epsonLanguage === 2 ? "Chinese - Simplified" : printer.epsonLanguage === 3 ? "Chinese - Traditional" : printer.epsonLanguage === 4 ? "Korean" : printer.epsonLanguage === 5 ? "Thai" : printer.epsonLanguage === 6 ? "Vietnamese" : "Multilingual"}</TableCell>
                <TableCell>{printer.tags && printer.tags.join(", ")}</TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleOpenEditDialog(printer)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      onClick={() => handleOpenDeleteDialog(printer)}
                      color="secondary"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow> 
            ))}
          </TableBody>
        </Table>
        <ZoneManagement floors={floors} printers={printers} />
      </Box>

      {/* Dialog for Create/Edit Printer */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Printer' : 'Add New Printer'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="printerName"
            label="Printer Name"
            type="text"
            fullWidth
            value={currentPrinter.printerName}
            onChange={handleInputChange}
            error={!!nameError}
            helperText={nameError}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Device Type</InputLabel>
            <Select
              name="deviceName"
              value={isCustomDevice ? "other" : currentPrinter.deviceName}
              onChange={handleInputChange}
            >
               <MenuItem value="TM-T82IV">TM-T82IV</MenuItem>
              <MenuItem value="TM-T82III">TM-T82III</MenuItem>
              <MenuItem value="TM-T82IIIL">TM-T82IIIL</MenuItem>
              <MenuItem value="TM-T82II">TM-T82II</MenuItem>
              <MenuItem value="TM-m30">TM-m30</MenuItem>
             
              <MenuItem value="TM-m30II">TM-m30II</MenuItem>
              <MenuItem value="TM-T20">TM-T20</MenuItem>
              <MenuItem value="TM-T88VI">TM-T88VI</MenuItem>
              <MenuItem value="TM-T88V">TM-T88V</MenuItem>
              <MenuItem value="TM-T82">TM-T82</MenuItem>
              <MenuItem value="TM-m30II-H">TM-m30II-H</MenuItem>
              <MenuItem value="TM-U220">TM-U220</MenuItem>
              <MenuItem value="other">Other (Custom)</MenuItem>
            </Select>
          </FormControl>
          
          {isCustomDevice && (
            <TextField
              margin="dense"
              name="customDeviceName"
              label="Enter Custom Device Name"
              type="text"
              fullWidth
              value={customDeviceName}
              onChange={handleInputChange}
            />
          )}
          
          <TextField
            margin="dense"
            name="ipAddress"
            label="IP Address"
            type="text"
            fullWidth
            value={currentPrinter.ipAddress}
            onChange={handleInputChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Printer Type</InputLabel>
            <Select
              name="brand"
              value={currentPrinter.brand}
              onChange={handleInputChange}
            >
              <MenuItem value="EPSON">EPSON</MenuItem>
              <MenuItem value="BIXOLON">BIXOLON</MenuItem>
              <MenuItem value="GPRINTER">GPRINTER</MenuItem>
              <MenuItem value="ZEBRA">ZEBRA</MenuItem>
              <MenuItem value="GPRINTER_LABEL">GPRINTER LABEL</MenuItem>
              <MenuItem value="ESCPOSPrinter">ESCPOSPrinter</MenuItem>
            </Select>
          </FormControl>
          
          {currentPrinter.brand === "ESCPOSPrinter" && (
            <TextField
              margin="dense"
              name="port"
              label="Port"
              type="number"
              inputProps={noWheelInputProps}
              fullWidth
              value={currentPrinter.port}
              onChange={handleInputChange}
              defaultValue={"9100"}
            />
          )}
          
          {currentPrinter.brand === "EPSON" && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Language</InputLabel>
              <Select
                name="epsonLanguage"
                value={currentPrinter.epsonLanguage}
                onChange={handleInputChange}
              >
                <MenuItem value={0}>English (EPOS2_LANG_EN)</MenuItem>
                <MenuItem value={1}>Japanese (EPOS2_LANG_JA)</MenuItem>
                <MenuItem value={2}>Chinese - Simplified (EPOS2_LANG_ZH_CN)</MenuItem>
                <MenuItem value={3}>Chinese - Traditional (EPOS2_LANG_ZH_TW)</MenuItem>
                <MenuItem value={4}>Korean (EPOS2_LANG_KO)</MenuItem>
                <MenuItem value={5}>Thai (EPOS2_LANG_TH)</MenuItem>
                <MenuItem value={6}>Vietnamese (EPOS2_LANG_VI)</MenuItem>
                <MenuItem value={7}>Multilingual (EPOS2_LANG_MULTI)</MenuItem>
              </Select>
            </FormControl>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={currentPrinter.isPrimary}
                onChange={(e) => {
                  setCurrentPrinter(prev => ({
                    ...prev,
                    isPrimary: e.target.checked
                  }));
                }}
                color="primary"
              />
            }
            label="Primary Printer"
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleSavePrinter} 
            color="primary"
            disabled={
              !currentPrinter.printerName || 
              !currentPrinter.ipAddress || 
              (isCustomDevice && !customDeviceName) ||
              !!nameError ||
              createMutation.isLoading ||
              updateMutation.isLoading
            }
          >
            {(createMutation.isLoading || updateMutation.isLoading) 
              ? 'Saving...' 
              : (isEditMode ? 'Update' : 'Create')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the printer "{printerToDelete?.printerName}"?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePrinter} 
            color="secondary"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default POSPrinterSettings;
